import { AppThunkAction } from './appThunkAction';
import api from '../../helpers/api';
import {
	getAllCreateJobSteps,
	getCallFieldMappingValidationResult,
	getCallFieldMappingResult,
	getCreateJobName, isCreateJobStepValid
} from '../selectors/createJobSelectors';
import {
	CallFieldMappingConfig, 
	CallLogData,
	CreateJobStep,
	GatherInfoStep, 
	JobModeDirectionCallFieldMappingConfig,
	ParsedCallLogFile
} from '../state/createJobState';
import {
	AxiosError
} from 'axios';
import { History } from 'history';
import { progressBarCompletingPause } from '../../helpers/constants';
import { findByType } from '../../helpers/utilities';
import { JobCreate, JobSummary } from '../../models/dto/job';
import { getUid } from '../../helpers/ids';
import { CallDirection } from '../../models/dto/callDirection';
import { CustomFieldSetValue, CustomFieldValueSummary } from '../../models/dto/customField';
import {
	CallDetails,
	CreateCall
} from '../../models/dto/call';
import { toast } from 'react-toastify';

export function initializeCreateJob(engagementId: number, customFields: CustomFieldValueSummary[] | nundefined): InitializeCreateJob {
	return { type:'create-job/initialize', engagementId, customFields: customFields ?? [] };
}

export function clearCreateJob(engagementId: number): ClearCreateJob {
	return { type:'create-job/clear', engagementId };
}

export function updateCreateJobName(engagementId: number, name: string): UpdateCreateJobName {
	return { type: 'create-job/update-name', engagementId, name };
}

export function updateCreateJobCustomField(engagementId: number, fieldId: number, value: string): UpdateCreateJobCustomField {
	return { type: 'create-job/update-custom-field', engagementId, fieldId, value };
}

export function previousCreateJobStep(engagementId: number): PreviousCreateJobStep {
	return { type: 'create-job/previous', engagementId };
}

export function nextCreateJobStep(engagementId: number,): NextCreateJobStep {
	return { type: 'create-job/next', engagementId };
}

export function callLogFileImported(engagementId: number, file: ParsedCallLogFile): CallLogFileImported {
	return { type: 'create-job/call-log-file-imported', engagementId, file };
}

export function removeCallLogFile(engagementId: number, id: string): RemoveCallLogFile {
	return { type: 'create-job/remove-call-log-file', engagementId, id };
}

export function chooseCallLogXlsFileSheet(engagementId: number, fileId: string, sheetId: string): ChooseCallLogXlsFileSheet {
	return { type: 'create-job/choose-call-log-xls-file-sheet', engagementId, fileId, sheetId };
}

export function updateCallFieldMappingConfig(engagementId: number, config: CallFieldMappingConfig): UpdateCallFieldMappingConfig {
	return { type: 'create-job/update-call-field-mapping-config', engagementId, config };
}

export function clearCallFieldMapping(engagementId: number, mappingId: string): ClearCallFieldMapping {
	return { type: 'create-job/clear-call-field-mapping', engagementId, mappingId };
}

export function clearCallFieldMappingByColumnIndex(engagementId: number, columnIndex: number): ClearCallFieldMappingByColumnIndex {
	return { type: 'create-job/clear-call-field-mapping-by-column-index', engagementId, columnIndex };
}

export function createJob(history: History, engagementId: number): AppThunkAction {
	return (dispatch, getState) => {
		const state = getState().createJobsState.byEngagement.get(engagementId);
		
		if (!state || state.createRequestStatus !== 'none' || state.currentStep.type !== 'mapCallFields' || !isCreateJobStepValid(state.currentStep, state.callLogData))
			return;
		
		const steps = getAllCreateJobSteps(state),
			data = getCleanedData(state.callLogData, state.currentStep.mappings),
			fieldsStep = state.currentStep,
			gatherInfoStep = findByType<GatherInfoStep, CreateJobStep>(steps, s => s.type === 'info' ? s : null),
			calls = data.callRows.map<Partial<CreateCall>>(row => {
				const call: Partial<CreateCall> = {},
					customFieldValues: CustomFieldSetValue[] = [];
				
				fieldsStep.mappings.forEach(mapping => {
					const result = getCallFieldMappingResult(row, mapping);
					const values = result.type === 'single' ? [result.value] : result.values;

					values.forEach(value => {
						if (value.type === 'none')
							return;

						if (value.type === 'invalid') {
							// result should always be valid since the UI prevents
							// users from moving to next step if it's not. so this
							// should never happen
							throw new Error('Encountered an invalid date while constructing new job details.');
						}

						if (value.destination.type === 'field') {
							call[value.destination.field] = value.value?.trim();
						} else {
							customFieldValues.push({ fieldId: value.destination.fieldId, value: value.value?.trim() });
						}
					});
				});
				
				call.customFieldValues = customFieldValues;

				return call;
			});
		
		const request: JobCreate = {
			name: getCreateJobName(state),
			calls: calls as CreateCall[],
			engagementId,
			direction: findByType<JobModeDirectionCallFieldMappingConfig, CallFieldMappingConfig>(fieldsStep?.mappings, mapping => mapping.type === 'direction' && mapping.modeConfig.type === 'job' ? mapping.modeConfig : null)?.direction ?? CallDirection.Unknown,
			audioChannel: null,
			createdWithoutCallManifest: false,
			customFieldValues: gatherInfoStep?.customFields.flatMap<CustomFieldSetValue>(field => {
				if (field.value && field.value !== field.defaultValue) {
					return {
						fieldId: field.fieldId,
						value: field.value
					}
				} else {
					return [];
				}
			})
		};

		dispatch({type: 'create-job/update-request-status', status: 'creating', engagementId});
		api.post<JobSummary>(`/api/jobs`, request).then(response => {
			dispatch({type: 'create-job/update-request-status', status: 'finishing', engagementId});

			// give the progress bar time to finish up
			setTimeout(() => {
				dispatch({type: 'create-job/update-request-status', status: 'complete', engagementId});
				history.push(`/engagements/${engagementId}/jobs/${response.data.id}`)
			}, progressBarCompletingPause);
		}).catch(error => {
			dispatch({type: 'create-job/update-request-status', status: 'none', engagementId});
			let errorMessage = error?.response?.data?.title?.replace(/\.$/, ':') + "\n";
			let rawErrors = error?.response?.data?.errors;
			if (rawErrors && typeof rawErrors === 'object') {
				const formatted = Object.entries(rawErrors)
					.map(([field, messages]) => `* ${field}: ${messages}.join(', ')}`)
					.join('\n');

				if (formatted) {
					errorMessage += formatted;
				}
			}
			console.log('An error occured when adding a job: ', errorMessage);
			toast.error(errorMessage, { style: { whiteSpace: 'pre-line' }, autoClose: 20000 });
		});
	};
}

function getCleanedData(original: CallLogData, mappings: CallFieldMappingConfig[]): CallLogData {
	const callRowsToRemove: Set<number> = new Set();

	mappings.forEach(m => {
		getCallFieldMappingValidationResult(m, original).validations.forEach(validation => {
			if (validation.type === 'warning' && validation.callRowsToRemove) {
				validation.callRowsToRemove.forEach(value => {
					callRowsToRemove.add(value);
				});
			}
		});
	});

	if (callRowsToRemove.size > 0) {
		const newCallRows: string[][] = [];

		original.callRows.forEach((row, i) => {
			if (!callRowsToRemove.has(i)) {
				newCallRows.push(row);
			}
		});
		
		return { ...original, callRows: newCallRows };
	} else {
		return original;
	}
}

export function getCallLogFileId():	string  {
    return getUid();
}

interface InitializeCreateJob {
    type: 'create-job/initialize';
    engagementId: number;
    customFields: CustomFieldValueSummary[];
}

interface ClearCreateJob {
    type: 'create-job/clear'; 
    engagementId: number;
}

interface UpdateCreateJobName {
    type: 'create-job/update-name';
	name: string;
	engagementId: number;
}

interface UpdateCreateJobCustomField {
    type: 'create-job/update-custom-field';
	value: string;
	fieldId: number;
	engagementId: number;
}

interface PreviousCreateJobStep {
    type: 'create-job/previous';
	engagementId: number;
}

interface NextCreateJobStep {
    type: 'create-job/next';
	engagementId: number;
}

interface CallLogFileImported {
    type: 'create-job/call-log-file-imported';
	engagementId: number;
	file: ParsedCallLogFile;
}

interface RemoveCallLogFile {
    type: 'create-job/remove-call-log-file';
    id: string;
	engagementId: number;
}

interface ChooseCallLogXlsFileSheet {
    type: 'create-job/choose-call-log-xls-file-sheet';
    fileId: string;
    sheetId: string;
	engagementId: number;
}

export interface UpdateCallFieldMappingConfig {
    type: 'create-job/update-call-field-mapping-config';
	config: CallFieldMappingConfig;
	engagementId: number;
}

interface ClearCallFieldMapping {
	type: 'create-job/clear-call-field-mapping';
	mappingId: string;
	engagementId: number;
}

interface ClearCallFieldMappingByColumnIndex {
    type: 'create-job/clear-call-field-mapping-by-column-index';
	columnIndex: number;
	engagementId: number;
}

interface UpdateCreateJobRequestStatus {
    type: 'create-job/update-request-status';
	status: 'none' | 'creating' | 'finishing' | 'complete';
	engagementId: number;
}

export type CreateJobActions = InitializeCreateJob | ClearCreateJob | UpdateCreateJobName | UpdateCreateJobCustomField 
	| PreviousCreateJobStep | NextCreateJobStep | CallLogFileImported | RemoveCallLogFile | ChooseCallLogXlsFileSheet 
	| UpdateCallFieldMappingConfig | ClearCallFieldMapping | UpdateCreateJobRequestStatus | ClearCallFieldMappingByColumnIndex;